import Cookies from 'js-cookie';

/*
 * Where the signed-in account summary is cached between page loads.
 *
 * It used to live in a `summaryData` cookie. The summary carries every API key
 * in full, and a cookie is readable by any script on the page, sent to the
 * server with every request, kept on disk after the tab closes, and capped at
 * ~4 KB (a large summary silently failed to save). It now lives in
 * sessionStorage: this tab only, never sent over the network, gone when the tab
 * closes. A new tab simply refetches the summary, which the app already does
 * whenever none is cached.
 *
 * The one-hour expiry the cookie had is kept, so a stale summary is never used
 * past the life of the session token.
 */
const KEY = 'mediasfu.summaryData';
const LEGACY_COOKIE = 'summaryData';
const TTL_MS = 3600 * 1000;

const removeLegacyCookie = () => {
  try {
    if (Cookies.get(LEGACY_COOKIE) !== undefined) Cookies.remove(LEGACY_COOKIE);
  } catch (error) {
    // Nothing to clean up.
  }
};

export const readStoredSummary = () => {
  removeLegacyCookie();
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { value, expiresAt } = JSON.parse(raw);
    if (!value || (expiresAt && Date.now() > expiresAt)) {
      window.sessionStorage.removeItem(KEY);
      return null;
    }
    return value;
  } catch (error) {
    return null;
  }
};

/** The stored summary as a JSON string, for callers that parse it themselves. */
export const readStoredSummaryRaw = () => {
  const summary = readStoredSummary();
  return summary ? JSON.stringify(summary) : undefined;
};

export const storeSummary = (summary) => {
  removeLegacyCookie();
  try {
    if (!summary) {
      window.sessionStorage.removeItem(KEY);
      return;
    }
    window.sessionStorage.setItem(KEY, JSON.stringify({ value: summary, expiresAt: Date.now() + TTL_MS }));
  } catch (error) {
    // Storage full or blocked: the summary still lives in memory for this page.
  }
};

export const clearStoredSummary = () => {
  removeLegacyCookie();
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (error) {
    // Nothing stored.
  }
};
